import React, { useState, useRef, useEffect } from 'react';
import { SectionList } from '@wmfe/mach-pro-components-react';
import SlideUpModal from '@/components/SlideUpModal';
import clsx from 'clsx';
import {
  EnumOrderType,
  IOrderDTO,
  IOrderList,
  EnumOrderHelpeScene
} from '../../type';
import { getEffectiveButtonList } from '../../utils';
import OrderItem from '../OrderItem';
import { getOrderList, initOrder } from '../../api';
import i18nClient from '../../locales/i18n.config';
import './index.css';
import { lxViewReport } from '../../lib/lx';
import { EnumLxPositionName } from '../../lib/constant';

interface Props {
  onModalSpreadChange: Function;
  OrderListPopSpread: boolean;
  onOrderClick: (order: IOrderDTO) => void;
  visitId?: string;
  from?: string;
  orderType?: EnumOrderType;
  orderHelperScene?: EnumOrderHelpeScene;
}

const compensationHeight = 44;

const OrderPopup: React.FC<Props> = ({
  onModalSpreadChange, OrderListPopSpread, onOrderClick, visitId, from, orderType, orderHelperScene = EnumOrderHelpeScene.Home
}) => {
  const [list, setList] = useState<IOrderDTO[]>([]);
  const [popoverTitle, setPopoverTitle] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [lastOrder, setLastOrder] = useState<string | undefined>(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const lock = useRef(false);
  // const finished = useRef(false);
  const error = useRef(false);
  const closeSlideUpModal = () => {
    onModalSpreadChange(false);
  };

  const getList = async () => {
    if (lock.current) return;
    lock.current = true;
    setLoading(true);
    try {
      const [err, res] = await getOrderList({
        pageSize,
        pageNo,
        lastOrderId: lastOrder,
        cursorMap: cursor,
        visitId,
      });

      const {
        hasMore, popoverTitle, orderList, lastOrderId, orderType, cursorMap
      } = (res as any) || ({} as IOrderList);
      if (pageNo === 1) {
        // this.$emit('changeTitle', popoverTitle);
        setPopoverTitle(popoverTitle);
      }
      // @ts-ignore
      // const newList = list.concat(orderList);
      const newList = orderList.map((item) => ({
        ...item,
        orderType
      }));
      setPageNo((pre) => pre + 1);
      setList((pre) => ([
        ...pre,
        ...newList
      ]));
      setLastOrder(lastOrderId);
      setCursor(cursorMap);
      setHasMore(Boolean(hasMore));
      setLoading(false);

      // MV 埋点
      newList.forEach((order: IOrderDTO) => {
        if ('extend' in order && order.extend?.isShowTranslateButton) {
          lxViewReport('c_oversea_helpcenter_rqhfdnvm', 'b_oversea_helpcenter_kirii8m1_mv', {
            event_name: '[machPro]问题答案页面翻译按钮曝光',
            order_id: order.orderId,
            order_type: EnumOrderType.Rider,
            position_name: EnumLxPositionName.OrderCard,
            visitId,
            from,
            bizline: order?.bizline,
          });
        }
      });
    } catch {
      error.current = true;
    } finally {
      setLoading(false);
      lock.current = false;
    }
  };

  useEffect(() => {
    if (OrderListPopSpread && !list?.length) getList();
  }, [OrderListPopSpread]);

  const handleOrderChange = async (order) => {
    try {
      const orderInfo = await initOrder({
        orderId: order?.orderId,
        orderHelperScene, // 从订单过来的是 2
        visitId,
        bizline: order?.bizline,
      });
      if (!orderInfo) {
        console.log('handleOrderChange-initOrder-error');
        return;
      }
      const { orderHelperDTOList } = orderInfo as any;
      onOrderClick({
        ...order,
        orderHelperDTOList: getEffectiveButtonList(orderHelperDTOList) || [],
      });
    } catch (error) {
      console.log('Error in handleOrderChange:', error);
    }
  };

  const handleOrderClick = (order) => {
    if (!order || !order?.orderId) {
      return;
    }
    handleOrderChange(order);
    onModalSpreadChange(false);
  };

  const renderModalItem = (itemData) => {
    const isLastedOne = itemData?.indexPath?.item / 1 === list.length - 1;
    const isFirstOne = itemData?.indexPath?.item === 0;
    const order = itemData?.data as IOrderDTO;

    return (
      <view onClick={() => onModalSpreadChange(false)}>
        <view
          className={clsx(
            orderType === EnumOrderType.Order ? 'order-item-card' : 'order-item-card-bd',
            orderType === EnumOrderType.KeemartRider && 'keemart-rider-order',
            isFirstOne ? 'order-item-card-first' : '',
            isLastedOne ? 'order-item-card-last' : ''
          )}
          onClick={() => handleOrderClick(order)}
        >
          <OrderItem order={order} visitId={visitId} from={from} />
        </view>
      </view>
    );
  };

  // const sectionModal = [{
  //   key: 'order-card-list',
  //   data: list,
  // }];

  const maxHeight = Mach.env.screenHeight * 0.8;
  const minHeight = Mach.env.screenHeight * 0.23;

  return (
    <SlideUpModal slideUpModalStatus={OrderListPopSpread} closeSlideUpModal={closeSlideUpModal}>
      <view className="close-area" onClick={closeSlideUpModal}>
        <view className="close-area-icon-wrapper">
          <image className="close-area-icon" src="assets://shop/multi_sku_close_icon.png" />
        </view>
      </view>
      <view className="modal-container" style={{ maxHeight, minHeight }}>
        <view className="modal-title">
          <text className="modal-title-txt">{popoverTitle}</text>
        </view>
        <view
          className="content-list"
          style={{
            height: `${list?.length * 94 + compensationHeight}dp`,
          }}
        >
          {
            list?.length && (
              <SectionList
                sections={[
                  {
                    key: 'order-card-list',
                    data: list,
                  },
                ]}
                extraData={{ list, hasMore }}
                renderItem={renderModalItem}
                enableRefreshControl
                itemKey={(item: any) => item?.key || 'NULL'}
                loading={loading}
                hasMore={hasMore}
                noMoreTip={i18nClient.getText('cs_portal_theres_nothing_more_left', 'End of records')}
                onPullUp={getList}
                estimatedItemHeight={94}
                selfSizingEnabled
                loadMoreExtraHeight={200}
              />
            )
          }
          {
            !list?.length && !loading && (
              <view className="no-order-content">
                <text className="no-order-tip">{i18nClient.getText('cs_portal_theres_nothing_more_left', 'End of records')}</text>
              </view>
            )
          }

        </view>
      </view>
    </SlideUpModal>
  );
};

export default OrderPopup;
